import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';

import {KeyboardActionsEnum} from '../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {ConversationInquiryModel} from '../../../../../../libs/shared/core/src/models/api/conversationInquiriesModel';
import {hasConversationAiGraph} from '../../../../../../libs/shared/core/src/models/api/factModel';
import {globalNamespace} from '../../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {ViewerDataEventObject} from '../../../../../../libs/shared/core/src/models/tracking/objectNames/viewerDataEventObject';
import {
  TrackDataEvent,
  useDataEventTracking,
} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {KeyboardEventHandlers} from '../../../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {useClipboard} from '../../../../../../libs/shared/ui/src/hooks/clipboardHookHelpers';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {ActivityModel} from '../../../../../models/api/activityModel';
import {RelatedKnowledgeReference} from '../../../../../models/api/relatedKnowledgeModel';
import {SimilarInquiry} from '../../../../../models/api/similarInquiriesModel';
import {pinAndUnpinActivitiesAction} from '../../../../../store/data/conversations/pinActivitiesToConversationsActions';
import {getConversations} from '../../../../../store/data/dataSelectors';
import {makeGetInquiries} from '../../../../../store/data/inquiryLists/inquiryListsSelectors';
import {fetchInquiryListsAsyncAction} from '../../../../../store/data/inquiryLists/inquiryListsThunkActions';
import {makeGetPinnedActivities} from '../../../../../store/data/pinnedActivityLists/pinnedActivityListsSelectors';
import {fetchPinnedActivityListAsyncAction} from '../../../../../store/data/pinnedActivityLists/pinnedActivityListsThunkActions';
import {makeGetRelatedKnowledge} from '../../../../../store/data/relatedKnowledgeLists/relatedKnowledgeListsSelectors';
import {fetchRelatedKnowledgeListsAsyncAction} from '../../../../../store/data/relatedKnowledgeLists/relatedKnowledgeListsThunkActions';
import {makeGetSimilarInquiries} from '../../../../../store/data/similarInquiryLists/similarInquiryListsSelectors';
import {fetchSimilarInquiryListsAsyncAction} from '../../../../../store/data/similarInquiryLists/similarInquiryListsThunkActions';
import {expandActivityViewerAsyncAction} from '../../../../../store/panels/viewer/activity/activityViewerActions';
import {
  jumpToActivityAsyncAction,
  toggleSynopsisAction,
} from '../../../../../store/panels/viewer/viewerPanelActions';
import {SynopsisFiltersEnum} from '../../../../../store/panels/viewer/viewerPanelState';
import {SynopsisFailed} from './synopsisFailed';
import {SynopsisHeader} from './synopsisHeader';
import {buildSynopsisDataEvent} from './synopsisHelpers';
import {SynopsisInspector} from './synopsisInspector';
import {SynopsisSkeleton} from './synopsisSkeleton';
import {SynopsisViewer} from './synopsisViewer';
import {SynopsisViewerContext} from './synopsisViewerContext';

export interface SynopsisViewerContainerProps {
  panelId: ViewerPanelId;
  conversationId: number;
  isInPopup?: boolean;
  filter?: SynopsisFiltersEnum;
}

export const SynopsisViewerContainer: FC<SynopsisViewerContainerProps> = ({
  panelId,
  conversationId,
  isInPopup,
  filter,
}) => {
  const [status, setStatus] = useState(AsyncStatusesEnum.LOADING);
  const [retryCount, setRetryCount] = useState(0);
  const [feedbackData, setFeedbackData] = useState<Record<string, unknown> | undefined>(undefined);
  const [copyText, setCopyText] = useState('');

  const {trackDataEvent} = useDataEventTracking();
  const {copy} = useClipboard();

  const conversation = useAppSelector((state) => getConversations(state)[conversationId]);
  const isPinnedCommentsOnly = conversation ? !hasConversationAiGraph(conversation.facts) : undefined;

  const getPinnedActivities = useMemo(() => makeGetPinnedActivities(conversationId), [conversationId]);
  const pinnedActivities = useAppSelector(getPinnedActivities);

  const getInquiries = useMemo(() => makeGetInquiries(conversationId), [conversationId]);
  const inquiries = useAppSelector(getInquiries);

  const getSimilarInquiries = useMemo(() => makeGetSimilarInquiries(conversationId), [conversationId]);
  const similarInquiries = useAppSelector(getSimilarInquiries);

  const getRelatedKnowledge = useMemo(() => makeGetRelatedKnowledge(conversationId), [conversationId]);
  const relatedKnowledge = useAppSelector(getRelatedKnowledge);

  const dispatch = useAppDispatch();

  const onTrackDataEvent: TrackDataEvent = useCallback(
    (action, objectName, objectData) => {
      trackDataEvent(action, objectName, {
        conversation_id: conversationId,
        ...buildSynopsisDataEvent(filter, inquiries, pinnedActivities, similarInquiries, relatedKnowledge),
        ...(objectData ?? {}),
      });
    },
    [conversationId, filter, inquiries, pinnedActivities, relatedKnowledge, similarInquiries, trackDataEvent],
  );

  const onRequestClose = useCallback(() => {
    onTrackDataEvent(DataEventActionsEnum.COLLAPSE, ViewerDataEventObject.SYNOPSIS);
    dispatch(toggleSynopsisAction(panelId, false));
  }, [dispatch, onTrackDataEvent, panelId]);

  const onUnpinActivity = useCallback(
    (activityId: number) => {
      if (conversation?.id) {
        dispatch(pinAndUnpinActivitiesAction(conversation.id, {unpinActivityIds: [activityId]}));
      }
    },
    [dispatch, conversation?.id],
  );

  const onJumpToItem = useCallback(
    ({activityId, itemType}: {activityId: number; itemType: string}) => {
      onTrackDataEvent(DataEventActionsEnum.CLICK, ViewerDataEventObject.SYNOPSIS_ITEM, {
        activity_id: activityId,
        type: itemType,
      });

      // Close in popup so you see the activity you're jumping to.
      if (isInPopup) {
        onRequestClose();
      }

      dispatch(jumpToActivityAsyncAction(panelId, {id: activityId}));
      dispatch(expandActivityViewerAsyncAction(panelId, activityId));
    },
    [dispatch, isInPopup, onRequestClose, onTrackDataEvent, panelId],
  );

  const onSubmitFeedback = useCallback(
    (feedback: string) => {
      if (feedbackData) {
        onTrackDataEvent(DataEventActionsEnum.SUCCESS, ViewerDataEventObject.SYNOPSIS_INSIGHT_INCORRECT, {
          ...feedbackData,
          feedback,
        });
      }

      setFeedbackData(undefined);
    },
    [feedbackData, onTrackDataEvent],
  );

  useEffect(() => {
    // async status only tracks essential data.
    // Silently reload if we're only refreshing.
    if (!pinnedActivities && !inquiries) {
      setStatus(AsyncStatusesEnum.LOADING);
    }

    Promise.all([
      dispatch(fetchPinnedActivityListAsyncAction(conversationId)),
      dispatch(fetchInquiryListsAsyncAction(conversationId)),
    ])
      .then(() => setStatus(AsyncStatusesEnum.LOADED))
      .catch(() => setStatus(AsyncStatusesEnum.FAILED));

    // Optional data
    dispatch(fetchSimilarInquiryListsAsyncAction(conversationId));
    dispatch(fetchRelatedKnowledgeListsAsyncAction(conversationId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, conversationId, retryCount]);

  const context = useMemo(
    () => ({
      namespace: conversation?.namespace ?? globalNamespace,
      isInPopup: Boolean(isInPopup),
      onJumpToItem,
      isFeedbackOpen: Boolean(feedbackData),
      onFeedbackAction: setFeedbackData,
      onSubmitFeedback,
      onCloseFeedback: () => setFeedbackData(undefined),
      onUnpinActivity,
      onTrackDataEvent,
      setCopyText,
    }),
    [
      conversation?.namespace,
      feedbackData,
      isInPopup,
      onJumpToItem,
      onSubmitFeedback,
      onTrackDataEvent,
      onUnpinActivity,
    ],
  );

  // Track open event once everything is loaded.
  useTrackSynopsisOpenEvent(
    inquiries,
    pinnedActivities,
    similarInquiries,
    relatedKnowledge,
    onTrackDataEvent,
  );

  const [isInspectorOpen, setIsInspectorOpen] = useState(false);
  const keyHandlers: KeyboardEventHandlers = {
    // Close with Escape.
    [KeyboardActionsEnum.CLOSE]: onRequestClose,

    // Open inspector with Cmd+Shift+E
    [KeyboardActionsEnum.TRACE]: (event) => {
      event.stopPropagation();
      setIsInspectorOpen((current) => !current);
    },

    [KeyboardActionsEnum.COPY]: () => {
      copy(copyText);
    },
  };

  if (!conversation) {
    return null;
  }

  if (status === AsyncStatusesEnum.FAILED) {
    return <SynopsisFailed onRetryClick={() => setRetryCount((count) => count + 1)} />;
  }

  if (status === AsyncStatusesEnum.LOADED && inquiries && pinnedActivities) {
    return (
      <KeyboardShortcuts handlers={keyHandlers}>
        <SynopsisViewerContext.Provider value={context}>
          {isInspectorOpen && (
            <SynopsisInspector
              conversationId={conversationId}
              inquiries={inquiries}
              similarInquiries={similarInquiries}
              relatedKnowledge={relatedKnowledge}
              onRequestClose={() => setIsInspectorOpen(false)}
            />
          )}
          <SynopsisHeader onRequestClose={onRequestClose} isPinnedCommentsOnly={isPinnedCommentsOnly} />
          <SynopsisViewer
            conversation={conversation}
            inquiries={inquiries}
            pinnedActivities={pinnedActivities}
            similarInquiries={similarInquiries ?? {}}
            relatedKnowledge={relatedKnowledge ?? {}}
            panelId={panelId}
          />
        </SynopsisViewerContext.Provider>
      </KeyboardShortcuts>
    );
  }

  return <SynopsisSkeleton onRequestClose={onRequestClose} isPinnedCommentsOnly={isPinnedCommentsOnly} />;
};

/*
 * Helpers.
 */

function useTrackSynopsisOpenEvent(
  inquiries: ReadonlyArray<ConversationInquiryModel> | undefined,
  pinnedActivities: ReadonlyArray<ActivityModel> | undefined,
  similarInquiries: Record<string, ReadonlyArray<SimilarInquiry>> | undefined,
  relatedKnowledge: Record<string, ReadonlyArray<RelatedKnowledgeReference>> | undefined,
  onTrackDataEvent: TrackDataEvent,
) {
  const [didTrackOpen, setDidTrackOpen] = useState(false);

  // Wait until everything is loaded to report event so we can compute stats.
  if (inquiries && pinnedActivities && similarInquiries && relatedKnowledge && !didTrackOpen) {
    setDidTrackOpen(true);
  }

  // Report event once.
  useEffect(() => {
    if (didTrackOpen) {
      onTrackDataEvent(DataEventActionsEnum.EXPAND, ViewerDataEventObject.SYNOPSIS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [didTrackOpen]);
}
